import React from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { KeyboardReactInterface } from 'react-simple-keyboard';

import { useSelector } from 'hooks';

import { getLocation } from 'modules/router/selectors';
import { hasShareboxStyle } from 'modules/dealers/selectors';
import { getSelectedLanguage } from 'modules/translations/selectors';
import Language from 'types/LanguageEnum';
import { createDictionary } from 'utils/objectUtils';

import { Button } from 'components/ui';
import { ButtonType } from 'components/ui/Button';

import KeyboardInput from './KeyboardInput';
import ComposingState from './cache/composing';
import {
  getPortalContainer,
  WRITABLE_INPUT_TYPES,
  getDefaultCandidates,
} from './utils/KeyUtils';
import JapaneseKeyboard from './Custom/Japanese';
import DefaultKeyboard from './Custom/Default';
import type { KeyboardComponentProps } from 'components/Keyboard/types';
import { KeyboardLayoutType } from 'components/Keyboard/types';

import './index.css';
import { SPECIAL_KEYS } from './utils/constants';

const KEYBOARDS = createDictionary<Language, React.FC<KeyboardComponentProps>>({
  [Language.JA_JP]: JapaneseKeyboard,
}, DefaultKeyboard);

// Sets a value on a native input/textarea bypassing React's value tracker; the caller then
// dispatches an 'input' event so React picks it up. See https://stackoverflow.com/a/46012210
const setNativeValue = (element: HTMLInputElement | HTMLTextAreaElement, value: string): void => {
  const prototype = element instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  setter?.call(element, value);
};

const KeyboardModal = () => {
  const keyboardRef = React.useRef<KeyboardReactInterface>(null);
  const focusedInput = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const location = useSelector(getLocation);
  const selectedLanguage = useSelector(getSelectedLanguage);

  const [value, setValue] = React.useState('');
  const [show, setShow] = React.useState(false);
  const [initialValue, setInitialValue] = React.useState('');
  const [selection, setSelection] = React.useState<[number, number]>([0, 0]);
  const type = focusedInput.current?.dataset?.keyboardType || focusedInput.current?.type || '';
  const inputMode = focusedInput.current?.inputMode || '';
  const maxLength = focusedInput.current?.maxLength ?? -1;
  const rawLayout = focusedInput.current?.dataset?.layout;
  const layout = Object.values(KeyboardLayoutType).includes(rawLayout as KeyboardLayoutType)
    ? (rawLayout as KeyboardLayoutType)
    : KeyboardLayoutType.DEFAULT;

  const candidates = React.useMemo(
    () => getDefaultCandidates(selectedLanguage),
    [selectedLanguage],
  );

  const isNumericMode = ['numeric'].includes(inputMode);
  const isNumericInput = ['tel', 'number'].includes(type) || isNumericMode;
  const computedType = React.useMemo(()=> type === 'password' && isNumericMode ? 'number' : type, [type, isNumericMode]) ;

  const KeyboardComponent = isNumericInput ? DefaultKeyboard : KEYBOARDS[selectedLanguage];

  const focusInput = React.useCallback((input: HTMLInputElement | HTMLTextAreaElement) => {
    focusedInput.current = input;
    setValue(input.value);
    setInitialValue(input.value);
    setShow(true);
    input.blur();
  }, []);

  const onFocus = React.useCallback(({ target }: Event) => {
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      focusInput(target);
    }
  }, [focusInput]);

  const onClose = React.useCallback((text?: string) => {
    setShow(false);

    if (typeof text === 'string' && focusedInput.current) {
      setNativeValue(focusedInput.current, text);

      const event = new Event('input', { bubbles: true });
      focusedInput.current.dispatchEvent(event);
    }

    const portal = document.getElementById('keyboard-portal');
    if (portal) {
      document.body.removeChild(portal);
    }
  }, []);

  const handleChangeSelection = React.useCallback((newSelection: [number, number]) => {
    setSelection(newSelection);
  }, []);

  const handleKeyReleased = React.useCallback(
    (which: string) => {
      if (which === SPECIAL_KEYS.TAB) {
        onClose();
      }
    },
    [onClose],
  );

  const handleKeyboardInit = React.useCallback(
    (keyboard: KeyboardReactInterface) => {
      keyboard.setInput(value);
    },
    [value],
  );

  const handleCancel = React.useCallback(() => {
    onClose(initialValue);
  }, [onClose, initialValue]);

  const handleSend = React.useCallback(() => {
    onClose(value);
  }, [onClose, value]);

  const handleClear = React.useCallback(() => {
    setValue('');
    keyboardRef.current?.clearInput();
  }, [setValue]);

  const handleChange = React.useCallback(async (newValue: string) => {
    if (maxLength > 0 && newValue.length > maxLength) {
      keyboardRef.current?.setInput(value); // resync keyboard internal state
      return;
    }
    setValue(newValue);
  }, [maxLength, value]);

  const setRef = React.useCallback((ref: KeyboardReactInterface) => {
    keyboardRef.current = ref;
  }, []);

  React.useLayoutEffect(() => {
    let inputs: NodeListOf<HTMLInputElement>;

    const attachListeners = () => {
      inputs = document.querySelectorAll('input,textarea');
      inputs.forEach(input => {
        if (WRITABLE_INPUT_TYPES.includes(input.type) && input.id !== 'keyboard-input') {
          input.removeEventListener('focus', onFocus);
          input.addEventListener('focus', onFocus);
          input.removeEventListener('click', onFocus);
          input.addEventListener('click', onFocus);

          if (document.activeElement === input && focusedInput.current !== input) {
            focusInput(input);
          }
        }
      });
    };

    const observer = new MutationObserver(attachListeners);

    observer.observe(document.body, { childList: true, subtree: true });
    attachListeners();

    return () => {
      inputs?.forEach(input => {
        input.removeEventListener('focus', onFocus);
        input.removeEventListener('click', onFocus);
      });
      observer.disconnect();
    };
  }, [onFocus, focusInput]);

  React.useEffect(() => {
    setShow(false);
  }, [location]);

  React.useEffect(
    () => () => {
      ComposingState.reset();
    },
    [],
  );

  return (
    show &&
      createPortal(
        <div className="z-60 fixed top-0 bottom-0 left-0 right-0">
          <div className="fixed h-screen w-screen bg-gray-900/25" onClick={handleSend} />
          <div className="fixed w-screen bottom-0">
            <div className="keyboard-inputWrapper mb-24 flex w-full m-auto max-w-6xl !min-w-96 justify-center px-12">
              <div className="bg-input-bg p-4 flex flex-col gap-4 w-full rounded-card">
                <KeyboardInput
                  type={type}
                  value={value}
                  selection={selection ?? [0, 0]}
                  onClear={handleClear}
                />
                <div className="flex gap-4">
                  <Button
                    onClick={handleCancel}
                    className="w-full flex justify-center items-center"
                    type={ButtonType.PRIMARY}
                  >
                    <FormattedMessage id="cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button onClick={handleSend} className="w-full flex justify-center items-center">
                    <FormattedMessage id="confirm.title" defaultMessage="Confirm" />
                  </Button>
                </div>
              </div>
            </div>
            <div className="bg-input-bg p-4">
              <div className={classNames({ 'max-w-[500px] mx-auto': isNumericInput })}>
                <KeyboardComponent
                  type={computedType}
                  layoutType={layout}
                  language={selectedLanguage}
                  layoutCandidates={candidates}
                  keyboardRef={setRef}
                  onChange={handleChange}
                  onInit={handleKeyboardInit}
                  onKeyReleased={handleKeyReleased}
                  onSelectionChange={handleChangeSelection}
                />
              </div>
            </div>
          </div>
        </div>,
        getPortalContainer(),
      )
  );
};

// This is a safeguard to ensure that the keyboard is only displayed on Sharebox kiosks
const KeyboardModalWrapper = () => {
  const allowKeyboard = useSelector(hasShareboxStyle);
  return allowKeyboard && <KeyboardModal />;
};

export default KeyboardModalWrapper;
